"use client";

import EngagementSection from "@/app/event-detail/engagement/engagement";
import { useClickOutside } from "@/app/event-detail/hooks/use-click-outside";
import { useLegendFilterState } from "@/app/event-detail/hooks/use-legend-filter-state";
import { useSelectedGuessState } from "@/app/event-detail/hooks/use-selected-guess-state";
import { useTitleCompact } from "@/app/event-detail/hooks/use-title-compact";
import type { EventDetailContentProps } from "@/app/event-detail/types";
import EventChart from "@/components/charts/event-chart";
import { AboutSection } from "@/components/event-detail/about-section";
import { EventDetailHeader } from "@/components/event-detail/event-detail-header";
import { EventDetailTitle } from "@/components/event-detail/event-detail-title";
import { LegendFilterDialog } from "@/components/event-detail/legend-filter-dialog";
import { LegendSection } from "@/components/event-detail/legend-section";
import { GuessCard } from "@/components/event-detail/main-guess-card";
import { RulesSection } from "@/components/event-detail/rules-section";
import { TradeButtonBar } from "@/components/event-detail/trade-button-bar";
import { useAppRouter } from "@/utils/router";
import { get } from "@/utils/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

function isSameSortedGuessList(
    prev: Array<{ guessNo: string; title?: string }>,
    next: Array<{ guessNo: string; title?: string }>,
) {
    return prev.length === next.length
        && prev.every( ( item, index ) => item.guessNo === next[index]?.guessNo && item.title === next[index]?.title );
}

/**
 * 事件详情展示组件（纯展示层）
 * 负责：
 * 1. 渲染事件详情各个部分
 * 2. 管理 UI 状态（图例过滤、选中状态等）
 * 3. 处理滚动到指定竞猜项
 */
export function EventDetailContent( {
    eventData,
    guessList,
    // isSingleGuessDetailPage: 是单个竞猜的详情页(也就是第三级页面、路由为/single-guess-detail)
    isSingleGuessDetailPage,
    urlParams,
}: EventDetailContentProps ) {
    const router = useAppRouter();
    const queryClient = useQueryClient();
    const { eventNo, guessNo, comment } = urlParams;
    const hasScrolledToGuessRef = useRef( false );

    // State management hooks
    const { selectedGuessNo, selectedBetType } = useSelectedGuessState( eventNo, guessList, eventData );
    const { selectedGuessNos, setSelectedGuessNos, displayedGuessList } = useLegendFilterState( guessList, eventNo );

    // UI state hooks
    const titleCompactProgress = useTitleCompact( 40, 160 );
    const [ selectedChartRow, setSelectedChartRow ] = useState<any>( {} );
    const [ isLegendDialogOpen, setIsLegendDialogOpen ] = useState( false );
    const chartFilterContainerRef = useRef<HTMLDivElement>( null );
    const stableSortedGuessListRef = useRef<Array<{ guessNo: string; title?: string }>>( [] );
    const handleColumnSelect = useCallback( ( row: any ) => setSelectedChartRow( row ), [] );
    const handleFilterClick = useCallback( () => {
        if ( guessList.length >= 2 ) {
            setIsLegendDialogOpen( true );
        }
    }, [ guessList.length ] );

    const sortedGuessList = useMemo(
        () =>
            guessList.map( ( guess ) => ( {
                guessNo: get( guess, "guessNo", "" ),
                title: get( guess, "title", "" ),
            } ) ),
        [ guessList ],
    );

    const stableSortedGuessList = useMemo( () => {
        if ( !isSameSortedGuessList( stableSortedGuessListRef.current, sortedGuessList ) ) {
            stableSortedGuessListRef.current = sortedGuessList;
        }
        return stableSortedGuessListRef.current;
    }, [ sortedGuessList ] );

    // 点击外部关闭浮窗
    useClickOutside( chartFilterContainerRef, () => {
        if ( isLegendDialogOpen ) {
            setIsLegendDialogOpen( false );
        }
    } );

    // 滚动到指定的 guessNo（如果存在且没有 comment 参数）
    useEffect( () => {
        if ( isSingleGuessDetailPage ) return;
        if ( comment || !guessNo || hasScrolledToGuessRef.current ) {
            return;
        }

        if ( !eventData || !guessList.length ) {
            return;
        }

        const element = document.getElementById( `guess-${guessNo}` );
        if ( element ) {
            hasScrolledToGuessRef.current = true;
            setTimeout( () => {
                element.scrollIntoView( { behavior: "smooth", block: "center" } );
            }, 100 );
        }
    }, [ guessNo, comment, eventData, guessList ] );

    // 重置滚动标记
    useEffect( () => {
        hasScrolledToGuessRef.current = false;
    }, [ guessNo, eventNo ] );

    const guessOptionsForEngagement = useMemo(
        () =>
            guessList.map( ( guess ) => ( {
                guessNo: guess.guessNo,
                title: guess.title,
                mainFlag: get( guess, "mainFlag" ),
                yesDesc: get( guess, "yesDesc" ),
                noDesc: get( guess, "noDesc" ),
            } ) ),
        [ guessList ],
    );

    // 判断是否显示底部按钮
    // isSingleGuess: 是事件中只有单个竞猜的情况
    const isSingleGuess = guessList.length === 1;
    const singleGuess = isSingleGuess && guessList.length > 0 ? guessList[0] : null;
    const status = singleGuess ? get( singleGuess, "status", 0 ) : 0;
    const isEnded = status === 2 || status === 3;

    // 计算持仓情况
    const userYesShareQuantity = singleGuess ? get( singleGuess, "userYesShareQuantity", 0 ) : 0;
    const userNoShareQuantity = singleGuess ? get( singleGuess, "userNoShareQuantity", 0 ) : 0;
    const hasSellableShares = userYesShareQuantity > 0 || userNoShareQuantity > 0;

    // 刷新持仓数据的回调，当发生buy、sell、merge、split时需要触发
    const refreshHoldings = useCallback( () => {
        const targetGuessNo = singleGuess ? singleGuess.guessNo : guessNo;
        const targetEventNo = eventNo || get( eventData, "eventNo", "" );

        if ( targetGuessNo ) {
            queryClient.invalidateQueries( { queryKey: [ "guess-trade-list", targetGuessNo ] } );
            queryClient.invalidateQueries( { queryKey: [ "guess-open-orders", targetGuessNo ] } );
            queryClient.invalidateQueries( { queryKey: [ "guess-trade-history", targetGuessNo ] } );
            queryClient.invalidateQueries( { queryKey: [ "eventDetail", "byGuess", targetGuessNo ] } );
            queryClient.invalidateQueries( { queryKey: [ "eventChartData", "guess", targetGuessNo ] } );
            queryClient.invalidateQueries( { queryKey: [ "userGuessOrder", targetGuessNo ] } );
            queryClient.invalidateQueries( { queryKey: [ "guess-position-detail", targetGuessNo ] } );
        }

        if ( targetEventNo ) {
            queryClient.invalidateQueries( { queryKey: [ "eventDetail", "byEvent", targetEventNo ] } );
            queryClient.invalidateQueries( { queryKey: [ "eventChartData", "event", targetEventNo ] } );
        }
    }, [ singleGuess, guessNo, eventNo, eventData, queryClient ] );

    // 统一的容器类名
    const containerClassName = twMerge(
        isSingleGuess && !isSingleGuessDetailPage && "-mx-4 min-h-0",
        !isSingleGuess && "event-detail-scrollable",
        "flex flex-col flex-1 h-full",
    );

    // 统一的内容区类名
    // 单竞猜时需要内部滚动，这样底部按钮才能固定在底部
    const contentClassName = twMerge(
        isSingleGuess && "event-detail-scrollable flex-1 min-h-0 overflow-y-auto",
        ( isSingleGuess || isSingleGuessDetailPage ) && "px-4",
    );

    // 是否显示底部按钮
    const showTradeButton = isSingleGuess && singleGuess && !isEnded;
    const isInsetLayout = isSingleGuess && !isSingleGuessDetailPage;

    return (
        <div className={containerClassName}>
            <div className={contentClassName}>
                {/* Header */}
                <EventDetailHeader
                    isSingleGuessDetailPage={isSingleGuessDetailPage}
                    tradeAmount={eventData.tradeAmount}
                    onBack={router.back}
                    eventData={eventData}
                />

                {/* Title */}
                <EventDetailTitle
                    eventImageUrl={get( eventData, "eventImageUrl", "" )}
                    eventTitle={get( eventData, "eventTitle", "" )}
                    compactProgress={titleCompactProgress}
                    isSingleGuessDetailPage={isSingleGuessDetailPage}
                    isInset={isInsetLayout}
                />

                {/* Legend */}
                <LegendSection
                    displayedGuessList={displayedGuessList}
                    guessList={guessList}
                    selectedChartRow={selectedChartRow}
                />

                {/* Chart area */}
                <div ref={chartFilterContainerRef} className="relative">
                    <EventChart
                        eventNo={eventNo || ""}
                        guessNo={isSingleGuessDetailPage ? guessNo : null}
                        volume={String( eventData.tradeAmount )}
                        onColumnSelect={handleColumnSelect}
                        selectedGuessNos={selectedGuessNos}
                        sortedGuessList={stableSortedGuessList}
                        onFilterClick={sortedGuessList?.length > 4 ? handleFilterClick : undefined}
                    />

                    {/* Legend Filter Dialog - 绝对定位在图表下方 */}
                    {guessList.length >= 2 && (
                        <LegendFilterDialog
                            open={isLegendDialogOpen}
                            guessList={guessList}
                            selectedGuessNos={selectedGuessNos}
                            onSelectionChange={setSelectedGuessNos}
                        />
                    )}
                </div>

                {/* Option list */}
                <div className="mb-3">
                    {guessList.map( ( guess, index ) => (
                        <GuessCard
                            key={guess.guessNo}
                            guess={guess}
                            index={index}
                            event={eventData}
                            isSingle={guessList.length === 1}
                            isLast={index === guessList.length - 1}
                            selectedGuessNo={null}
                            selectedBetType={null}
                        />
                    ) )}
                </div>
                {/* About Section */}
                {!isSingleGuess && <AboutSection event={eventData} />}
                {!isSingleGuess && <RulesSection event={eventData} />}
                {/* {(!isSingleGuess || !isSingleGuessDetailPage) && <AboutSection event={eventData} />} */}

                {/* Rules Section */}
                {/* {(!isSingleGuess || !isSingleGuessDetailPage) && <RulesSection event={eventData} />} */}

                {/* Engagement section */}
                <EngagementSection
                    eventNo={eventNo || ""}
                    commentId={comment ?? undefined}
                    eventData={{
                        eventImageUrl: get( eventData, "eventImageUrl", "" ),
                        eventTitle: eventData.eventTitle,
                    }}
                    guessList={guessOptionsForEngagement}
                />
            </div>

            {/* 底部按钮栏 */}
            {showTradeButton && (
                <TradeButtonBar
                    guess={singleGuess}
                    event={eventData}
                    hasSellableShares={hasSellableShares}
                    onRefreshHoldings={refreshHoldings}
                />
            )}
        </div>
    );
}
